import { createInstancedRenderer } from "./instancedRenderer";
import { paintBand } from "../canvas2d/paintBand";

// Instance: [x0, y0, x1, y1] in data units — one segment of the upper edge
const BAND_ATTRIBUTE_COMPONENTS = [4];

// Each segment is a trapezoid from the segment down to uBottom; adjacent
// trapezoids share their vertical edges, so nothing is blended twice
const BAND_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec4 aSegment;

uniform vec2 uViewport;
uniform vec2 uX;
uniform vec2 uY;
uniform float uBottom;

out float vY;

void main() {
  float x = mix(aSegment.x, aSegment.z, aCorner.x) * uX.x + uX.y;
  float edgeY = mix(aSegment.y * uY.x + uY.y, aSegment.w * uY.x + uY.y, aCorner.x);
  float y = mix(edgeY, uBottom, aCorner.y);
  vec2 clip = vec2(x, y) / uViewport * 2.0 - 1.0;

  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vY = y;
}
`;

// Vertical gradient between two device-px rows, the same for every column;
// highp keeps whole rows on tall canvases
const BAND_FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform vec2 uGradientRows;
uniform vec4 uTopColor;
uniform vec4 uBottomColor;

in float vY;
out vec4 outColor;

void main() {
  float position = clamp((vY - uGradientRows.x) / (uGradientRows.y - uGradientRows.x), 0.0, 1.0);

  outColor = mix(uTopColor, uBottomColor, position);
}
`;

/**
 * Instanced band under a polyline filled with a vertical gradient: the area
 * of the Line chart.
 */
export const createBandRenderer = () => {
  const renderer = createInstancedRenderer({
    vertexShader: BAND_VERTEX_SHADER,
    fragmentShader: BAND_FRAGMENT_SHADER,
    uniformNames: [
      "uViewport",
      "uX",
      "uY",
      "uBottom",
      "uGradientRows",
      "uTopColor",
      "uBottomColor",
    ],
    attributeComponents: BAND_ATTRIBUTE_COMPONENTS,
  });

  return {
    floatsPerInstance: renderer.floatsPerInstance,
    init: renderer.init,
    setInstances: renderer.setInstances,
    updateInstances: renderer.updateInstances,

    /**
     * @param {object} params
     * @param {number[]} params.viewport   - [width, height] of the canvas, device px
     * @param {number[]} params.xTransform - [scale, offset]: data x → device px
     * @param {number[]} params.yTransform - [scale, offset]: data y → device px
     * @param {number} params.bottom - lower edge of the band, device px
     * @param {number[]} params.gradientRows - [top, bottom] rows of the gradient, device px
     * @param {number[]} params.topColor    - RGBA 0..1 at the top row and above
     * @param {number[]} params.bottomColor - RGBA 0..1 at the bottom row and below
     */
    draw: (params) => {
      const { viewport, xTransform, yTransform, bottom, gradientRows, topColor, bottomColor } = params;

      renderer.draw(
        (gl, uniforms) => {
          gl.uniform2fv(uniforms.uViewport, viewport);
          gl.uniform2fv(uniforms.uX, xTransform);
          gl.uniform2fv(uniforms.uY, yTransform);
          gl.uniform1f(uniforms.uBottom, bottom);
          gl.uniform2fv(uniforms.uGradientRows, gradientRows);
          gl.uniform4fv(uniforms.uTopColor, topColor);
          gl.uniform4fv(uniforms.uBottomColor, bottomColor);
        },
        (context, instances) => paintBand(context, instances, renderer.floatsPerInstance, params),
      );
    },
  };
};
